import { CREDIT_LIMITS } from './constants.js';

/**
 * Check if a course should be disabled based on selection state and credit limits
 * @param {Object} course - The course to check
 * @param {string} groupKey - The group key for the course
 * @param {Object} groupedCourses - All grouped courses
 * @param {Set} selectedIds - Set of selected course IDs
 * @param {Set} backupIds - Set of backup course IDs
 * @param {Set} selectedCourses - Set of selected course codes
 * @param {Object} semesterCredits - Credits per semester
 * @returns {boolean} - True if course should be disabled
 */
export const isCourseDisabled = (
  course, 
  groupKey, 
  groupedCourses, 
  selectedIds, 
  backupIds, 
  selectedCourses, 
  semesterCredits
) => {
  const courseId = course.is_split_course 
    ? `${course.semester_instance}_p${course.actual_period}` 
    : course.semester_instance;
  
  // Note: We no longer disable courses that are selected elsewhere, as the selection logic
  // now automatically handles this by going directly to backup state
    
  // Second check: Normal credit limit and selection logic (only for selected, not backup)
  if (course.is_split_course) {
    const allCourseParts = Object.values(groupedCourses)
      .flatMap(group => group.courses)
      .filter(c => c.is_split_course && c.semester_instance === course.semester_instance)
      .map(c => `${c.semester_instance}_p${c.actual_period}`);
    
    const anyPartSelected = allCourseParts.some(partId => selectedIds.has(partId));
    const anyPartBackup = allCourseParts.some(partId => backupIds.has(partId));
    
    if (anyPartSelected || anyPartBackup) {
      return false; // Already selected or backup courses are not disabled
    }
    
    // Check if selecting all parts would exceed the credit limit (only count selected courses)
    // For span courses, only count credits that belong to THIS semester
    const currentGroup = groupedCourses[groupKey];
    const semesterKey = `${currentGroup.year}_s${currentGroup.semester}`;
    
    const totalCreditsForThisSemester = Object.entries(groupedCourses)
      .filter(([key, group]) => `${group.year}_s${group.semester}` === semesterKey)
      .flatMap(([key, group]) => group.courses)
      .filter(c => 
        c.is_split_course && 
        c.semester_instance === course.semester_instance
      )
      .reduce((sum, c) => sum + c.credits, 0);
    
    const currentCredits = semesterCredits[semesterKey] || 0; // This only counts selected, not backup
    
    return currentCredits + totalCreditsForThisSemester > CREDIT_LIMITS.SEMESTER_MAX;
  } else {
    // Handle regular single-period courses
    if (selectedIds.has(courseId) || backupIds.has(courseId)) {
      return false; // Already selected or backup courses are not disabled
    }
    
    const group = groupedCourses[groupKey];
    const semesterKey = `${group.year}_s${group.semester}`;
    const currentCredits = semesterCredits[semesterKey] || 0; // This only counts selected, not backup
    
    return currentCredits + course.credits > CREDIT_LIMITS.SEMESTER_MAX;
  }
}; 